import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { LocaleContext } from "./LocaleContext";
import { DEFAULT_LOCALE, HTML_LANG, isSupportedLocale, type Locale } from "./supported";

const STORAGE_KEY = "farmly.locale";
const URL_PARAM = "lang";

/**
 * Resolves the initial locale at first render.
 *
 * Priority (highest first):
 *   1. URL `?lang=` query parameter
 *   2. `localStorage('farmly.locale')`
 *   3. `navigator.language` if it matches a supported locale
 *   4. `DEFAULT_LOCALE` (sv)
 *
 * Resolution is synchronous so the first render is correct and we avoid a
 * hydration flash. Safe in non-browser environments (SSR, vitest jsdom edge cases).
 */
function resolveInitialLocale(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;

  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(URL_PARAM);
    if (isSupportedLocale(fromUrl)) return fromUrl;
  } catch {
    // ignore — fall through to next source
  }

  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (isSupportedLocale(stored)) return stored;
  } catch {
    // localStorage may be blocked (private mode, embed contexts) — fall through
  }

  try {
    const nav = window.navigator?.language?.slice(0, 2).toLowerCase();
    if (isSupportedLocale(nav)) return nav;
  } catch {
    // ignore
  }

  return DEFAULT_LOCALE;
}

function writeLocaleToUrl(next: Locale): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (next === DEFAULT_LOCALE) {
      url.searchParams.delete(URL_PARAM);
    } else {
      url.searchParams.set(URL_PARAM, next);
    }
    window.history.replaceState(window.history.state, "", url.toString());
  } catch {
    // best-effort only
  }
}

function persistLocale(next: Locale): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(STORAGE_KEY, next);
  } catch {
    // ignore
  }
}

function updateHtmlLang(next: Locale): void {
  if (typeof document === "undefined") return;
  try {
    document.documentElement.lang = HTML_LANG[next];
  } catch {
    // ignore
  }
}

interface LocaleProviderProps {
  children: ReactNode;
  /** Optional override, primarily for tests. */
  initialLocale?: Locale;
}

/**
 * Provides the active locale to the rest of the app.
 *
 * Keeps three things in sync with the active locale:
 *   - `<html lang>`
 *   - `?lang=` query parameter (non-default only)
 *   - `localStorage('farmly.locale')`
 *
 * `setLocale` does NOT navigate — it patches the URL and re-renders subscribers.
 * Components that need the current value should use `useLocale()`.
 */
export function LocaleProvider({ children, initialLocale }: LocaleProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? resolveInitialLocale());

  useEffect(() => {
    updateHtmlLang(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState((prev) => {
      if (prev === next) return prev;
      persistLocale(next);
      writeLocaleToUrl(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
